import { useEffect, useState } from "react";
import { Button, Dropdown, Empty, List, Menu, Space, Spin, Typography } from "antd";
import { DownOutlined, DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { dateFormat } from "../../core/utils/date-utils";

/**
 * Bir çekimin ONAYLI raporları — mobil karttaki "Raporlar" düğmesinin açtığı liste.
 *
 * Liste, istem grid'iyle aynı uçtan (GetStudyRequestTableList) gelir; ayrı bir uç açılmadı, böylece
 * görünürlük/erişim kuralı tek yerde kalır. Onaysız raporlar ve raporsuz istemler elenir: buradaki
 * amaç hekimin/hastanın nihai raporu almasıdır, taslak göstermek değil.
 */
export default function StudyReportsModal({ studyId }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await ApiService.Request.GetStudyRequestTableList(studyId);
            if (res.success)
                setReports((res.data || []).filter(x => x.reportId && x.reportIsConfirm && !x.isRequestDeleted));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyId]);

    // PDF blob URL'i olarak döner; aynı URL hem yeni sekmede açmak hem indirmek için kullanılır.
    const getPdf = async (row, language) => {
        return language
            ? ApiService.ReportGenerator.GetReportPdfWithLanguage(studyId, row.id, row.reportId, language)
            : ApiService.ReportGenerator.GetReportPdf(studyId, row.id, row.reportId);
    }

    const openPdf = async (row, language) => {
        const res = await getPdf(row, language);
        if (!res.success)
            return;

        // Yeni sekme engellenirse (mobil tarayıcılarda olabiliyor) indirmeye düşülür — kullanıcı
        // düğmeye bastığında hiçbir şey olmamış gibi kalmasın.
        const tab = window.open(res.data, '_blank');
        if (!tab)
            ApiService.downloadFile(res.data, res.fileName);
    }

    const downloadPdf = async (row, language) => {
        const res = await getPdf(row, language);
        if (res.success)
            ApiService.downloadFile(res.data, res.fileName);
    }

    if (loading)
        return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;

    if (reports.length === 0)
        return <Empty description={t('No confirmed report')} />;

    return (
        <List
            dataSource={reports}
            renderItem={(row) => {
                // Birden fazla dil şablonu varsa dil seçimi açılır menüden; tek şablonda düz düğme.
                const languages = row.reportOutputTemplates?.length > 1 ? row.reportOutputTemplates : null;

                return (
                    <List.Item style={{ display: 'block', padding: '10px 0' }}>
                        <div style={{ marginBottom: 6 }}>
                            <Typography.Text strong>{row.requestTypeName || '-'}</Typography.Text>
                            <div style={{ fontSize: 12, color: '#888' }}>
                                {[row.accessionNumber, row.modalityName].filter(Boolean).join(' · ') || '-'}
                            </div>
                            <div style={{ fontSize: 12, color: '#888' }}>
                                {t('Confirm Date')}: {row.reportConfirmDate ? dateFormat(row.reportConfirmDate) : '-'}
                                {row.reportConfirmUser ? ` · ${row.reportConfirmUser}` : ''}
                            </div>
                        </div>

                        <Space wrap>
                            <Button
                                type={'primary'}
                                size={'small'}
                                icon={<FilePdfOutlined />}
                                onClick={() => openPdf(row)}
                            >
                                {t('Open PDF')}
                            </Button>
                            <Button
                                size={'small'}
                                icon={<DownloadOutlined />}
                                onClick={() => downloadPdf(row)}
                            >
                                {t('Download PDF')}
                            </Button>
                            {languages && (
                                <Dropdown
                                    trigger={['click']}
                                    overlay={(
                                        <Menu onClick={({ key }) => openPdf(row, key)}>
                                            {languages.map(temp => (
                                                <Menu.Item key={temp.language}>
                                                    {t(`lang_${temp.language}`)}
                                                </Menu.Item>
                                            ))}
                                        </Menu>
                                    )}
                                >
                                    <Button size={'small'}>
                                        {t('Language')} <DownOutlined />
                                    </Button>
                                </Dropdown>
                            )}
                        </Space>
                    </List.Item>
                );
            }}
        />
    );
}
